import { mostrarToast } from '@/lib/formularioGuiado';
import TextoAssistente from './TextoAssistente';
import { formatarHoraMensagem } from '../utils';
import type { MensagemAssistente } from '../types';

interface Props {
  mensagem: MensagemAssistente;
  aoTentarDeNovo?: () => void;
  /** Mostra os botões "Copiar" e "WhatsApp" embaixo da resposta (padrão: sim). */
  mostrarAcoes?: boolean;
  /** Toque na imagem enviada abre em tela cheia. */
  aoAbrirImagem?: (url: string) => void;
  /** Respostas rápidas (chips) logo abaixo desta bolha. */
  opcoes?: string[];
  aoEscolherOpcao?: (texto: string) => void;
}

function BolhaImagem({
  url,
  enviando,
  aoAbrir,
}: {
  url: string;
  enviando?: boolean;
  aoAbrir?: (url: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => aoAbrir?.(url)}
      disabled={enviando}
      aria-label="Ver imagem"
      className="relative mb-2 block w-full max-w-[240px] cursor-pointer overflow-hidden rounded-[14px] bg-black/5"
    >
      <img src={url} alt="Imagem enviada" className="h-auto w-full object-cover" />
      {enviando && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        </span>
      )}
    </button>
  );
}

export default function BolhaMensagem({
  mensagem,
  aoTentarDeNovo,
  mostrarAcoes = true,
  aoAbrirImagem,
  opcoes,
  aoEscolherOpcao,
}: Props) {
  const temOpcoes = !!opcoes && opcoes.length > 0 && !!aoEscolherOpcao;

  if (mensagem.papel === 'usuario') {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col items-end">
          <div className="rounded-[18px] rounded-br-[6px] bg-primary-600 px-4 py-2.5 text-white shadow-pcard">
            {mensagem.anexo_url && (
              <BolhaImagem
                url={mensagem.anexo_url}
                enviando={mensagem.enviandoAnexo}
                aoAbrir={aoAbrirImagem}
              />
            )}
            {mensagem.texto && (
              <p className="whitespace-pre-wrap break-words text-[14px] leading-relaxed">
                {mensagem.texto}
              </p>
            )}
          </div>
          <div className="mt-1 flex items-center justify-end gap-2 px-1">
            <span className="text-[11px] text-foreground-400">
              {formatarHoraMensagem(mensagem.criado_em)}
            </span>
            {mensagem.erro && aoTentarDeNovo && (
              <button
                type="button"
                onClick={aoTentarDeNovo}
                className="aperta cursor-pointer whitespace-nowrap rounded-full bg-danger-50 px-2.5 py-1 text-[11px] font-semibold text-danger-600"
              >
                Tentar de novo
              </button>
            )}
          </div>
          {mensagem.erro && (
            <p className="mt-0.5 px-1 text-right text-[12px] text-danger-600">{mensagem.erro}</p>
          )}
        </div>
      </div>
    );
  }

  function copiar() {
    navigator.clipboard?.writeText(mensagem.texto).catch(() => {
      /* sem permissão de recortar/colar: segue sem travar */
    });
    mostrarToast('Copiado', 1800, 'sucesso');
  }

  const linkWhatsapp = `https://wa.me/?text=${encodeURIComponent(mensagem.texto)}`;

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%]">
        <div className="rounded-[18px] rounded-bl-[6px] bg-background-50 px-4 py-3 shadow-pcard">
          {mensagem.pendente ? (
            <span className="flex items-center py-1" aria-label="Pensando" role="status">
              <span className="ponto-pensando text-foreground-400" />
              <span className="ponto-pensando text-foreground-400" style={{ animationDelay: '0.15s' }} />
              <span className="ponto-pensando text-foreground-400" style={{ animationDelay: '0.3s' }} />
            </span>
          ) : (
            <TextoAssistente texto={mensagem.texto} />
          )}
        </div>

        {temOpcoes && !mensagem.pendente && (
          <div className="mt-2 flex flex-wrap gap-2">
            {opcoes!.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => aoEscolherOpcao?.(o)}
                className="aperta flex min-h-[44px] cursor-pointer items-center whitespace-nowrap rounded-full bg-primary-50 px-4 text-[13px] font-semibold text-primary-700 transition-colors hover:bg-primary-100"
              >
                {o}
              </button>
            ))}
          </div>
        )}

        {!mensagem.pendente && (
          <div className="mt-1 flex flex-wrap items-center gap-3 px-1">
            <span className="text-[11px] text-foreground-400">
              {formatarHoraMensagem(mensagem.criado_em)}
            </span>
            {mostrarAcoes && (
              <>
                <button
                  type="button"
                  onClick={copiar}
                  className="aperta flex cursor-pointer items-center gap-1 whitespace-nowrap text-[12px] font-semibold text-foreground-500 transition-colors hover:text-foreground-700"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-file-copy-line" aria-hidden />
                  </span>
                  Copiar
                </button>
                <a
                  href={linkWhatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aperta flex cursor-pointer items-center gap-1 whitespace-nowrap text-[12px] font-semibold text-success-600 transition-colors hover:text-success-700"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-whatsapp-line" aria-hidden />
                  </span>
                  WhatsApp
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}